'use client'

import { useEffect, useState, useCallback, useRef } from 'react'

import classNames from 'classnames'
import useTranslation from 'next-translate/useTranslation'

const VIEWPORT_TRIGGER_RATIO = 0.1 // active zone top 10%
const NEAR_TOP_SCROLL_THRESHOLD = 50
const HEADING_ID_PREFIX = 'toc-heading-'

export const TABLE_OF_CONTENTS_WRAPPER_ID = 'table-of-contents-wrapper'

interface Heading {
  /**
   * The ID of the heading element to scroll to.
   */
  id: string
  /**
   * Text content.
   */
  text: string
  /**
   * The level of the heading. e.g. 2 for h2, 3 for h3.
   */
  level: number
}

interface TableOfContentsProps {
  /**
   * The ID of the element that contains the headings to be included in the table of contents.
   */
  wrapperId: string
  /**
   * Custom styling?
   */
  className?: string
}

// Checks if the element's span (top to bottom) includes the viewportTriggerY
const isElementInViewportCenter = (
  element: HTMLElement,
  viewportTriggerY: number,
): boolean => {
  const rect = element.getBoundingClientRect()
  return rect.top <= viewportTriggerY && rect.bottom >= viewportTriggerY
}

// Finds the last visible heading element whose top is at or above the viewportTriggerY.
const findLastElementAbovePoint = (
  headingData: Heading[],
  viewportTriggerY: number,
): string | null => {
  for (let i = headingData.length - 1; i >= 0; i--) {
    const heading = headingData[i]
    const element = document.getElementById(heading.id)
    // Ensure element exists and is visible (offsetParent check)
    if (element && element.offsetParent !== null) {
      const rect = element.getBoundingClientRect()
      if (rect.top <= viewportTriggerY) {
        return heading.id
      }
    }
  }
  return null
}

export const TableOfContents: React.FC<TableOfContentsProps> = ({
  wrapperId,
  className,
}) => {
  const { t } = useTranslation('common')

  const [headings, setHeadings] = useState<Heading[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)

  // This flag temporarily disables the scroll-spy right after the user clicks
  // a ToC link. Without it the scroll handler fires *before* the browser
  // finishes jumping to the hash, causing the highlight to flicker.
  const isNavigatingRef = useRef(false)

  /**
   * Updates URL hash, sets activeId and scrolls to the target element with an offset.
   */
  const handleLinkClick = useCallback(
    (id: string) => () => {
      isNavigatingRef.current = true
      setActiveId(id)

      // release lock shortly after the browser completes the native jump
      setTimeout(() => {
        isNavigatingRef.current = false
      }, 50)
    },
    [],
  )

  /**
   * Scans the DOM within the wrapperId for h2 and h3 elements,
   * filters for visible ones, and updates the headings state.
   */
  const getWrapperHeadings = useCallback(() => {
    const wrapperElement = document.getElementById(wrapperId)
    if (!wrapperElement) {
      setHeadings([])
      return
    }

    const headingElements = Array.from(
      wrapperElement.querySelectorAll<HTMLElement>('h2, h3'),
    )

    // Filter for elements that are actually rendered and visible
    const visibleHeadings = headingElements.filter(
      (el) => el.offsetParent !== null,
    )

    const newHeadings = visibleHeadings.map((headingElement, index) => {
      const text = headingElement.textContent || ''
      let id = headingElement.id
      // If a heading doesn't have an ID, generate a unique one and assign it
      if (!id) {
        id = `${HEADING_ID_PREFIX}${Date.now()}-${index}`
        headingElement.id = id
      }

      return {
        id,
        text,
        level: parseInt(headingElement.tagName.substring(1), 10),
      }
    })

    // Update headings state only if the content has actually changed.
    // This prevents potential infinite loops with MutationObserver and unnecessary re-renders.
    setHeadings((currentHeadings) => {
      if (JSON.stringify(currentHeadings) === JSON.stringify(newHeadings)) {
        return currentHeadings
      }
      return newHeadings
    })
  }, [wrapperId])

  /**
   * Handles scroll events to determine the currently active heading based on its position
   * in the viewport and updates the activeId state.
   */
  const handleScroll = useCallback(() => {
    if (headings.length === 0 || isNavigatingRef.current) return

    const viewportTriggerY = window.innerHeight * VIEWPORT_TRIGGER_RATIO
    let newActiveId: string | null = null

    // PRIMARY RULE ‑ heading intersecting the trigger line
    for (const heading of headings) {
      const element = document.getElementById(heading.id)
      if (
        element &&
        element.offsetParent !== null && // Ignore hidden elements
        isElementInViewportCenter(element, viewportTriggerY)
      ) {
        newActiveId = heading.id
        break
      }
    }

    // GAP RULE ‑ none intersect, pick last heading above the line
    if (newActiveId === null) {
      newActiveId = findLastElementAbovePoint(headings, viewportTriggerY)
    }

    // TOP-OF-PAGE RULE ‑ near the top, default to first visible heading
    if (newActiveId === null && window.scrollY < NEAR_TOP_SCROLL_THRESHOLD) {
      const firstVisibleHeading = headings.find((h) => {
        const el = document.getElementById(h.id)
        return el && el.offsetParent !== null
      })
      if (firstVisibleHeading) {
        newActiveId = firstVisibleHeading.id
      }
    }

    // Only update activeId if newActiveId is not null and different from current,
    // OR if newActiveId is null but there are no headings (to clear highlight).
    // This prevents clearing the highlight if no rule matches but headings exist.
    if (newActiveId !== null || headings.length === 0) {
      setActiveId((currentActiveId) =>
        currentActiveId === newActiveId ? currentActiveId : newActiveId,
      )
    }
  }, [headings])

  // Observe DOM changes within the wrapper to re-scan headings if content changes
  // (e.g. tabs for CCTP V1 and V2)
  useEffect(() => {
    getWrapperHeadings()

    const wrapperElement = document.getElementById(wrapperId)
    if (!wrapperElement) return

    const observer = new MutationObserver(() => {
      getWrapperHeadings()
    })

    observer.observe(wrapperElement, {
      childList: true,
      subtree: true,
      attributes: true,
    })

    return () => {
      observer.disconnect()
    }
  }, [wrapperId, getWrapperHeadings])

  // Handling scroll state functionality to the table of contents
  useEffect(() => {
    if (headings.length === 0) return

    handleScroll() // Initial check to set active heading on page load
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [headings, handleScroll])

  if (headings.length === 0) {
    return null
  }

  return (
    <nav
      aria-label="Table of contents"
      className={classNames(
        'xl:overflow-y-scroll xl:max-h-[calc(100vh-theme(spacing.header)-theme(spacing.14))]',
        className,
      )}
      role="navigation"
    >
      <p className="text-neutral-subtle type-body-sm-bold">
        {t('tableOfContents.inThisArticle')}
      </p>
      <ul className="w-58 list-none">
        {headings.map((heading, index) => {
          const isActive = activeId === heading.id
          const previousHeading = index > 0 ? headings.at(index - 1) : null
          const isFirstH3AfterH2 =
            heading.level === 3 && previousHeading?.level === 2

          return (
            <li
              key={heading.id}
              className={classNames('truncate', {
                'mt-1 py-0.5': heading.level === 2,
                'pl-4 border-l border-neutral-subtle py-0.5 first:pt-0 last:pb-0':
                  heading.level === 3,
                'mt-1 pt-0': isFirstH3AfterH2,
              })}
            >
              <a
                className={classNames(
                  'py-1 transition-colors duration-100 ease-in-out type-body-sm hover:text-neutral-strong',
                  {
                    'text-neutral': heading.level === 2 && !isActive,
                    'text-neutral-subtle': heading.level === 3 && !isActive,
                    'text-neutral-strong type-body-sm-bold': isActive,
                  },
                )}
                href={`#${heading.id}`}
                onClick={handleLinkClick(heading.id)}
              >
                {heading.text}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
